<template>
  <div class="hotel-result card mb-4" @click="gotoHotelDetails">
    <div class="card-content">
      <div class="columns is-multiline">
        <!-- Hotel Image Column -->
        <div class="column is-12-mobile is-5-tablet">
          <figure class="image hotel-image">
            <img :src="image" class="is-rounded" />
          </figure>
        </div>

        <!-- Hotel Details Column -->
        <div class="column is-12-mobile is-5-tablet">
          <span v-if="isPreferredVendor" class="tag is-success"
            ><i class="fa fa-solid fa-star mr-1" /> Preferred Hotel
          </span>

          <!-- Hotel Name -->
          <h2 class="hotel-name">{{ result.hotelName }}</h2>

          <!-- Address -->
          <div class="hotel-address">
            {{ result.address.AddressLine }}
          </div>

          <!-- Distance -->
          <div class="distance-info" v-if="result.coordinates.distanceFromSearchedLocation">
            <b-icon icon="map-marker" size="is-small"></b-icon>
            <span class="is-lowercase has-text-grey">
              {{ result.coordinates.distanceFromSearchedLocation }} miles away
            </span>
          </div>

          <!-- Amenities Tag -->
          <div class="b-taglist">
            <b-tag
              v-for="(amenity, index) in availableAmenities"
              :key="index"
              type="is-primary is-light"
              class="mr-2"
            >
              <i
                :class="amenity.icon"
                class="text-surface-400 mr-1"
                aria-hidden="true"
              />
              <span>{{ amenity.label }}</span>
            </b-tag>
          </div>

          <!-- Travel Policy Tag -->
          <template v-if="result.travelPolicy">
            <out-of-policy-tag
              v-if="policyViolationsCount > 0"
              position="is-bottom"
              :travel-policy="result.travelPolicy"
              class="mt-1"
            ></out-of-policy-tag>
          </template>

          <!-- Immediate Travel Warning -->
          <template v-if="hasImmediateWarning">
            <immediate-travel-note
              :severity="hasImmediateWarning"
            ></immediate-travel-note>
          </template>
        </div>

        <!-- Price Column -->
        <div class="column is-12-mobile is-2-tablet">
          <div v-if="loading" class="price-skeleton">
            <b-skeleton width="70%" :active="true"></b-skeleton>
            <b-skeleton width="80%" :active="true"></b-skeleton>
            <b-skeleton width="25%" :active="true"></b-skeleton>
          </div>

          <div
            v-else-if="cheapestPrice > 0"
            class="price-display"
            data-test="show_price"
          >
            <p class="has-text-grey is-size-7">approx</p>
            <p class="price-amount has-text-info">${{ cheapestPrice }}</p>
            <p class="has-text-grey is-size-7">per night</p>
          </div>

          <div v-else-if="roomsCount == 0" class="no-rooms">
            <p class="has-text-grey is-size-7">
              No rooms found for dates selected.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OutOfPolicyTag from "@/components/Tags/OutOfPolicyTag"
import ImmediateTravelNote from "@/components/Traveller/ImmediateTravelNote"
import { mapState } from "vuex"

export default {
  components: {
    OutOfPolicyTag,
    ImmediateTravelNote,
  },

  props: {
    result: {
      type: Object,
      required: true,
    },
    cacheKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
    }
  },

  computed: {
    ...mapState({
      profile: (state) => state.travellersAndTrips.currentTravellerProfile,
      searchForm: (state) => state.hotelSearch.searchForm,
      amenityOptions: (state) => state.hotelSearch.filters.amenities.options,
    }),

    availableAmenities() {
      return this.amenityOptions.filter((i) => {
        return this.result.description?.amenities.includes(i.value)
      })
    },

    image() {
      let url = "https://ganttravel.tripcircuit.com/images/defaultHotel.jpg"
      let hotel = this.result?.description?.imagesAndDescriptions?.hotel

      if (hotel?.images?.[0]?.url) {
        url = hotel.images[0].url
      }
      return url
    },

    cheapestPrice() {
      if (this.result.cheapestPrice) {
        return Number(this.result.cheapestPrice.toFixed(0))
      }
      return 0
    },

    rooms() {
      return this.result.rooms
    },

    roomsCount() {
      return this.rooms?.length
    },
    policyViolationsCount() {
      return this.result.travelPolicy?.summary.rulesTriggeredCount || 0
    },

    hasImmediateWarning() {
      if (!this.rooms?.length) return null

      for (let room of this.rooms) {
        if (room.immediateTravelBookingAdvice) {
          return room.immediateTravelBookingAdvice
        }
      }
      return null
    },

    isPreferredVendor() {
      return this.result.isPreferred
    },
  },

  methods: {

    /*
    getLiveRates() {
      this.loading = true

      let hotelPrograms = this.profile.travelerPrograms
      let hotelChainCode = this.result.chainCode

      let rateCodes = hotelPrograms
        .filter(
          (item) =>
            item.rateCode &&
            item.type === "Hotel" &&
            (item.providerCode === hotelChainCode ||
              item.relatedProviderCodes.includes(hotelChainCode))
        )
        .map((program) => program.rateCode)
        .filter((code, index, self) => self.indexOf(code) === index)

      const body = {
        stateful: true,
        sessionKey: this.session ?? null,
        requestedCurrency: "USD",
        normalizeResponse: true,
        travelerContactId: this.profile.id,
        travelPolicyId: this.profile.travelPolicyId,
        hotelCode: this.result.hotelCode,
        checkInDate: this.searchForm.checkinDate,
        checkOutDate: this.searchForm.checkoutDate,
        roomCount: 1,
        guestCount: 1,
        ratePlanCodes: rateCodes,
      }

      this.$app
        .huddleLabsServerCall("amadeus/hotel/live-search", body)
        .then((result) => {
          this.$emit("setLiveData", {
            data: result.data,
            hotelCode: this.result.hotelCode,
            session: result.meta?.sessionKey,
          })
          this.loading = false
        })
    },
    */

    gotoHotelDetails() {
      this.$store.dispatch("hotelSearch/saveHotel", this.result)

      if (this.pathMixin_isMeeting) {
        this.$nav.pushRouteTo({
          name: "MeetingHotelDetail",
          params: { hotelCode: this.result.hotelCode },
          query: { key: this.cacheKey }
        })
      } else {
        this.$nav.pushRouteTo({
          name: "hotel-detail",
          params: { hotelCode: this.result.hotelCode, key: this.cacheKey },
          query: { key: this.cacheKey }
        })
      }
    },
  },

  /*
    watch: {
      autoloadPrice(newVal) {
        if (newVal === true) {
          this.getLiveRates()
        }
      }
    },
  
    mounted() {
      if (this.autoloadPrice && !this.rooms) {
        this.getLiveRates()
      }
    }
    */
}
</script>

<style scoped>
.hotel-result {
  cursor: pointer;
  transition: box-shadow 0.3s ease;
  border: 1px solid #dcdcdd;
  --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
    0 4px 6px -4px var(--tw-shadow-color);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}

.hotel-result:hover {
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.2),
    0 0 0 1px rgba(10, 10, 10, 0.02);
}

.hotel-image img {
  object-fit: cover;
  max-height: 240px;
  width: 100%;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hotel-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #4a4a4a;
  margin-bottom: 0.5rem;
}

.hotel-address {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.distance-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.amenities {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.amenity-tag {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #7957d5;
}

.price-display {
  text-align: right;
}

.price-amount {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin: 0.25rem 0;
}

.price-skeleton {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.no-rooms {
  text-align: right;
}

@media screen and (max-width: 768px) {
  .price-display,
  .price-skeleton,
  .no-rooms {
    text-align: left;
    margin-top: 1rem;
  }

  .hotel-name {
    font-size: 1.25rem;
  }
}
</style>
